import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import usePageTitle from '../../hooks/usePageTitle';
import { Link, useParams } from 'react-router-dom';
import { axiosInstance } from "../../routes/axiosInstance";
import PublicNav from '../../components/PublicNav';
import PublicFooter from '../../components/PublicFooter';

export default function EmailVerification() {
  usePageTitle('Email verification');
  const { uidb64, token } = useParams();
  const [status, setStatus] = useState(null); // null = loading, true = success, false = failure
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const response = await axiosInstance.get(`/user/verify/?uid=${uidb64}&token=${token}`);
        if (response.status === 200) {
          setStatus(true);
        }
      } catch (error) {
        setStatus(false);
        setError('Invalid or expired verification token.');
      }
    };
    verifyEmail().then();
  }, [uidb64, token]);

  return (
    <div className="min-h-screen bg-surface dark:bg-gray-870 text-slate-900 dark:text-gray-100 flex flex-col">
      <PublicNav/>

      <main className="flex-1 flex items-center justify-center w-full">
        <div className="max-w-md mx-auto px-6 py-16 w-full">
          <div className="text-center">
            <h1 className="text-xl sm:text-2xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white">
              Email <span className="italic text-teal-700 dark:text-teal-400">verification</span>.
            </h1>
          </div>

          <div className="mt-10 rounded-2xl bg-surface/70 dark:bg-gray-870/60 backdrop-blur-sm border border-slate-900/10 dark:border-gray-100/10 p-6 sm:p-8">
            <div className="text-center" role="status" aria-live="polite">
              {status === null && (
                <p className="text-sm text-slate-600 dark:text-gray-300">
                  Verifying your email, please wait...
                </p>
              )}

              {status === true && (
                <>
                  <CheckCircleIcon className="mx-auto h-16 w-16 text-green-600 dark:text-green-400" aria-hidden="true"/>
                  <h2 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                    Email verified!
                  </h2>
                  <p className="mt-3 text-sm text-slate-600 dark:text-gray-300">
                    Your email has been successfully verified. You can now sign in.
                  </p>
                </>
              )}

              {status === false && (
                <>
                  <XCircleIcon className="mx-auto h-16 w-16 text-red-600 dark:text-red-400" aria-hidden="true"/>
                  <h2 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                    Email verification failed
                  </h2>
                  <p className="mt-3 text-sm text-slate-600 dark:text-gray-300">
                    {error ? error : 'Something went wrong. Please try again.'}
                  </p>
                </>
              )}
            </div>

            {status !== null && (
              <div className="mt-8 text-center">
                <Link
                  to="/signin"
                  className="flex w-full justify-center items-center rounded-md bg-slate-900 text-white dark:bg-white dark:text-gray-900 px-4 py-2 text-sm font-medium h-10 shadow hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white"
                >
                  Go to sign in
                </Link>
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <Link to="/"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition">
              <ArrowLeftIcon className="h-4 w-4"/>
              Back to homepage
            </Link>
          </div>
        </div>
      </main>

      <PublicFooter/>
    </div>
  );
}
